import * as THREE from 'three';
import type * as Physics from '@dimforge/rapier3d-compat';
import type { CharacterRig } from './character-rig.js';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import type { HitImpact, HitMotion } from './character-hit.js';
import { terrainGrid, terrainHeight } from '../../../shared/game/terrain.js';
import { deathRagdoll as tuning } from '../../../content/characters/rigs/death-ragdoll.js';
import { humanoidDeathRig, type DeathRig } from './death-rig.js';
import { deathHitImpulse } from './death-impulse.js';

let physics: typeof Physics | undefined, loading: Promise<void> | undefined;
const active = new Set<DeathRagdoll>();
/** Required by every gameplay figure, loaded once during visual preparation. */
export function prepareDeathRagdolls(): Promise<void> {
  return loading ??= import('@dimforge/rapier3d-compat').then(async module => {
    // The browser gets the `.wasm` build (`scripts/rapier-wasm.mjs`), ready on import; only the compat build (Node) needs `init`.
    await module.init?.(); physics = module; warmPhysics(module);
  });
}
/**
 * One small fall stepped while the scene prepares: the solver's first step ever (contact, joints and continuous collision compiling and
 * allocating) took 30–70 ms, a hitch the moment the first body of a fight fell.
 */
function warmPhysics(R: typeof Physics) {
  const world = new R.World({ x: 0, y: -9.81, z: 0 });
  try {
    world.timestep = tuning.step;
    world.createCollider(R.ColliderDesc.trimesh(new Float32Array([-2, 0, -2, 2, 0, -2, -2, 0, 2, 2, 0, 2]), new Uint32Array([0, 2, 1, 1, 2, 3])).setCollisionGroups(GROUND));
    world.createCollider(R.ColliderDesc.cuboid(2, .1, 2).setTranslation(0, -.1, 0).setCollisionGroups(GROUND));
    const body = (y: number) => world.createRigidBody(R.RigidBodyDesc.dynamic().setTranslation(0, y, 0).setLinearDamping(tuning.linearDamping).setAngularDamping(tuning.angularDamping).setCcdEnabled(true));
    const [a, b, c, d] = [.3, .7, 1.1, 1.5].map(body);
    world.createCollider(R.ColliderDesc.capsule(.15, .08).setMass(5).setCollisionGroups(BODY), a);
    world.createCollider(R.ColliderDesc.ball(.1).setMass(3).setCollisionGroups(BODY), b);
    world.createCollider(R.ColliderDesc.capsule(.15, .08).setMass(5).setCollisionGroups(BODY), c);
    world.createCollider(R.ColliderDesc.capsule(.15, .08).setMass(5).setCollisionGroups(BODY), d);
    const revolute = world.createImpulseJoint(R.JointData.revolute({ x: 0, y: .2, z: 0 }, { x: 0, y: -.2, z: 0 }, { x: 1, y: 0, z: 0 }), a, b, true) as Physics.RevoluteImpulseJoint;
    revolute.setLimits(-tuning.hingeTravel, tuning.hingeTravel); revolute.setContactsEnabled(false);
    world.createImpulseJoint(R.JointData.spherical({ x: 0, y: .2, z: 0 }, { x: 0, y: -.2, z: 0 }), b, c, true).setContactsEnabled(false);
    world.createImpulseJoint(R.JointData.fixed({ x: 0, y: .2, z: 0 }, { x: 0, y: 0, z: 0, w: 1 }, { x: 0, y: -.2, z: 0 }, { x: 0, y: 0, z: 0, w: 1 }), c, d, true).setContactsEnabled(false);
    a.applyImpulse({ x: 1, y: 0, z: 0 }, true); a.setAngvel({ x: 2, y: 0, z: 0 }, true);
    for (let i = 0; i < 4; i++) world.step();
  } finally { world.free(); }
}

interface Segment {
  bone: THREE.Object3D; body: Physics.RigidBody; offset: THREE.Vector3;
  from: THREE.Vector3; rotation: THREE.Quaternion;
}
interface Saved { bone: THREE.Object3D; position: THREE.Vector3; rotation: THREE.Quaternion; scale: THREE.Vector3 }
/** The transforms a ragdoll restores when it is disposed (the pose it was built from), handed from one ragdoll to the next. */
export type RagdollRest = readonly Saved[];
/**
 * `rest` keeps the transforms of an earlier ragdoll (a Prone body that then dies keeps the standing pose it fell from), and `topple: false`
 * leaves out the initial tip that stops an upright body balancing on its feet (a body already on the ground). `motion` carries the living flinch velocities.
 */
export interface RagdollOptions { rest?: RagdollRest; topple?: boolean; motion?: HitMotion }
// Collision groups (membership << 16 | filter): a body ignores its own segments, meets the ground and the other body.
const BODY = 0x00010006, GROUND = 0x00020005, RIDER = { body: 0x00040003, straddle: 0x00040002 } as const;
const vector = (v: Physics.Vector) => new THREE.Vector3(v.x, v.y, v.z);
const quaternion = (q: Physics.Rotation) => new THREE.Quaternion(q.x, q.y, q.z, q.w);

/** An isolated, bounded world per falling body: no corpse piles or gameplay collisions. */
export class DeathRagdoll {
  private world?: Physics.World;
  private segments: Segment[] = [];
  private saved: Saved[] = [];
  private accumulator = 0;
  private age = 0;
  private quiet = 0;
  private headCollider?: Physics.Collider;
  settled = false;

  static create(rig: CharacterRig, ground: ((x: number, z: number) => number) | undefined,
    map: MapDefinition | undefined, direction: Point, impact: HitImpact | undefined, severed: boolean) {
    return this.createRig(humanoidDeathRig(rig), ground, map, direction, impact, severed);
  }
  static createRig(rig: DeathRig, ground: ((x: number, z: number) => number) | undefined,
    map: MapDefinition | undefined, direction: Point, impact: HitImpact | undefined, severed: boolean, options: RagdollOptions = {}) {
    if (!physics) throw new Error('Prepare death physics before updating gameplay figures.');
    if (active.size >= tuning.activeLimit) active.values().next().value!.settle();
    const doll = new DeathRagdoll(rig);
    try { doll.build(ground, map, direction, impact, severed, options); if (options.rest) doll.saved = [...options.rest]; return doll; }
    catch (error) { doll.dispose(); throw error; }
  }
  private constructor(private rig: DeathRig) {}

  private build(ground: ((x: number, z: number) => number) | undefined, map: MapDefinition | undefined,
    direction: Point, impact: HitImpact | undefined, severed: boolean, options: RagdollOptions) {
    const R = physics!;
    this.world = new R.World({ x: 0, y: -9.81, z: 0 }); active.add(this);
    this.world.timestep = tuning.step;
    this.rig.root.updateWorldMatrix(true, true);
    const controlled = new Set(this.rig.segments.map(segment => segment.bone));
    this.rig.root.traverse(bone => {
      if ((bone instanceof THREE.Bone || controlled.has(bone)) && !bone.userData.nimblezSkinSupport)
        this.saved.push({ bone, position: bone.position.clone(), rotation: bone.quaternion.clone(), scale: bone.scale.clone() });
    });
    const origin = this.rig.root.getWorldPosition(new THREE.Vector3());
    this.ground(origin, ground, map);
    const add = (bone: THREE.Object3D, end: THREE.Object3D | THREE.Vector3, radius: number, mass: number, sphere = false, localStart?: THREE.Vector3, rider?: keyof typeof RIDER) => {
      const boneOrigin = bone.getWorldPosition(new THREE.Vector3()), rotation = bone.getWorldQuaternion(new THREE.Quaternion());
      const start = localStart ? bone.localToWorld(localStart.clone()) : boneOrigin;
      const scale = bone.getWorldScale(new THREE.Vector3()).x;
      const finish = end instanceof THREE.Object3D ? end.getWorldPosition(new THREE.Vector3()) : bone.localToWorld(end.clone());
      const center = start.clone().add(finish).multiplyScalar(.5);
      const offset = center.clone().sub(boneOrigin).applyQuaternion(rotation.clone().invert());
      const length = start.distanceTo(finish), r = radius * scale;
      const body = this.world!.createRigidBody(R.RigidBodyDesc.dynamic().setTranslation(center.x, center.y, center.z)
        .setRotation(rotation).setLinearDamping(tuning.linearDamping).setAngularDamping(tuning.angularDamping).setCcdEnabled(true));
      const shape = sphere ? R.ColliderDesc.ball(r) : R.ColliderDesc.capsule(Math.max(.005, length / 2 - r), r);
      // Capsule local Y follows the segment even when the neck/torso rest axis is offset.
      const axis = finish.clone().sub(start).normalize().applyQuaternion(rotation.clone().invert());
      shape.setRotation(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis))
        .setMass(mass).setFriction(tuning.friction).setRestitution(0).setCollisionGroups(rider ? RIDER[rider] : BODY);
      const head = bone === this.rig.head;
      if (!head || !severed) { const collider = this.world!.createCollider(shape, body); if (head) this.headCollider = collider; }
      else body.setAdditionalMass(mass, true);
      const segment = { bone, body, offset, from: center.clone(), rotation: rotation.clone() };
      this.segments.push(segment); return segment;
    };
    const join = (a: Segment, b: Segment, anchorBone: THREE.Object3D, hinge = false, fixed = false) => {
      const point = anchorBone.getWorldPosition(new THREE.Vector3());
      const anchor = (s: Segment) => point.clone().sub(vector(s.body.translation())).applyQuaternion(quaternion(s.body.rotation()).invert());
      const aQ = quaternion(a.body.rotation()), bQ = quaternion(b.body.rotation());
      // Frame equality at capture makes coarse hinge limits relative to the actual death pose.
      const bFrame = bQ.clone().invert().multiply(aQ);
      const data = fixed ? R.JointData.fixed(anchor(a), new THREE.Quaternion(), anchor(b), bFrame)
        : hinge ? R.JointData.revolute(anchor(a), anchor(b), { x: 1, y: 0, z: 0 }) : R.JointData.spherical(anchor(a), anchor(b));
      const joint = this.world!.createImpulseJoint(data, a.body, b.body, true);
      joint.setContactsEnabled(false);
      if (hinge) {
        joint.setFrameX2(bFrame);
        (joint as Physics.RevoluteImpulseJoint).setLimits(-tuning.hingeTravel, tuning.hingeTravel);
      }
    };
    const segments = new Map(this.rig.segments.map(s => [s.bone, add(s.bone, s.end, s.radius, s.mass, s.sphere, s.start, s.rider)]));
    for (const j of this.rig.joints) join(segments.get(j.parent)!, segments.get(j.child)!, j.anchor, j.kind === 'hinge', j.kind === 'fixed');
    for (const s of this.segments) s.body.recomputeMassPropertiesFromColliders();
    const totalMass = this.segments.reduce((sum, s) => sum + s.body.mass(), 0);
    const motion = deathHitImpulse(direction, impact, totalMass);
    for (const s of this.segments) {
      const share = s.body.mass() / totalMass;
      const inherited = options.motion?.get(s.bone);
      if (inherited) s.body.setLinvel(inherited.linear.clone().multiplyScalar(tuning.flinchCarry).clampLength(0, tuning.maxFlinchSpeed), true);
      s.body.applyImpulse({ x: motion.impulse.x * share, y: -.15 * s.body.mass(), z: motion.impulse.z * share }, true);
      // Keep the existing horizontal speed limit even when a flinch contributes extra motion.
      const velocity = s.body.linvel(), speed = Math.hypot(velocity.x, velocity.z);
      if (speed > tuning.maxHitSpeed) s.body.setLinvel({ x: velocity.x * tuning.maxHitSpeed / speed, y: velocity.y, z: velocity.z * tuning.maxHitSpeed / speed }, true);
      const spin = inherited ? inherited.angular.clone().multiplyScalar(tuning.flinchCarry).clampLength(0, tuning.maxFlinchSpin) : new THREE.Vector3();
      // A small initial topple ensures a motionless upright body does not balance on its feet.
      if (options.topple ?? true) spin.add(new THREE.Vector3(motion.direction.z * tuning.toppleSpeed, 0, -motion.direction.x * tuning.toppleSpeed));
      s.body.setAngvel(spin, true);
    }
  }

  private ground(origin: THREE.Vector3, sample: ((x: number, z: number) => number) | undefined, map?: MapDefinition) {
    const R = physics!, world = this.world!;
    if (!map?.terrain && !sample) {
      world.createCollider(R.ColliderDesc.cuboid(tuning.groundReach, .1, tuning.groundReach)
        .setTranslation(origin.x, origin.y - .1, origin.z).setFriction(tuning.friction).setCollisionGroups(GROUND));
    } else {
      const grid = map ? terrainGrid(map) : undefined;
      const dx = grid ? map!.width / grid.columns : .5, dz = grid ? map!.depth / grid.rows : .5;
      const x0 = grid ? Math.floor((origin.x + map!.width / 2 - tuning.groundReach) / dx) * dx - map!.width / 2 : origin.x - tuning.groundReach;
      const z0 = grid ? Math.floor((origin.z + map!.depth / 2 - tuning.groundReach) / dz) * dz - map!.depth / 2 : origin.z - tuning.groundReach;
      const cols = Math.ceil(tuning.groundReach * 2 / dx) + 1, rows = Math.ceil(tuning.groundReach * 2 / dz) + 1;
      const vertices: number[] = [], indices: number[] = [];
      for (let z = 0; z <= rows; z++) for (let x = 0; x <= cols; x++) {
        const wx = x0 + x * dx, wz = z0 + z * dz;
        vertices.push(wx, map?.terrain ? terrainHeight(map, wx, wz) : sample!(wx, wz), wz);
      }
      for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
        const a = z * (cols + 1) + x, b = a + 1, c = a + cols + 1, d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
      world.createCollider(R.ColliderDesc.trimesh(new Float32Array(vertices), new Uint32Array(indices))
        .setFriction(tuning.friction).setCollisionGroups(GROUND));
    }
    for (const obstacle of map?.obstacles ?? []) {
      if (!['rock', 'building', 'structure', 'ruin'].includes(obstacle.kind) || !obstacle.height ||
        Math.abs(obstacle.x - origin.x) > tuning.groundReach + obstacle.width / 2 || Math.abs(obstacle.z - origin.z) > tuning.groundReach + obstacle.depth / 2) continue;
      world.createCollider(R.ColliderDesc.cuboid(obstacle.width / 2, obstacle.height / 2, obstacle.depth / 2)
        .setTranslation(obstacle.x, (sample?.(obstacle.x, obstacle.z) ?? origin.y) + obstacle.height / 2, obstacle.z)
        .setFriction(tuning.friction).setCollisionGroups(GROUND));
    }
  }

  sever() {
    if (this.world && this.headCollider) {
      // A colliderless body with no mass would become immovable through its fixed neck joint.
      this.segments.find(segment => segment.bone === this.rig.head)?.body.setAdditionalMass(2, true);
      this.world.removeCollider(this.headCollider, true); this.headCollider = undefined;
    }
  }
  update(elapsed: number) {
    if (!this.world || this.settled || elapsed <= 0) return;
    this.accumulator += Math.min(elapsed, tuning.step * tuning.maxSteps);
    while (this.accumulator + 1e-9 >= tuning.step) {
      for (const s of this.segments) { s.from.copy(vector(s.body.translation())); s.rotation.copy(quaternion(s.body.rotation())); }
      this.world.step(); this.accumulator -= tuning.step; this.age += tuning.step;
      const still = this.segments.every(s => vector(s.body.linvel()).length() < tuning.linearQuiet && vector(s.body.angvel()).length() < tuning.angularQuiet);
      this.quiet = still ? this.quiet + tuning.step : 0;
      if (this.quiet >= tuning.quietSeconds || this.age >= tuning.maxSeconds) break;
    }
    const finish = this.quiet >= tuning.quietSeconds || this.age >= tuning.maxSeconds;
    this.project(finish ? 1 : Math.max(0, this.accumulator) / tuning.step);
    if (finish) { this.settled = true; this.release(); }
  }
  /** Joining corpses and budget pressure still obtain their pose from the same physics solver. */
  settle() {
    if (!this.world) return;
    while (this.age < tuning.maxSeconds) { this.world.step(); this.age += tuning.step; }
    this.project(1); this.settled = true; this.release();
  }
  private project(alpha: number) {
    for (const s of this.segments) {
      const rotation = s.rotation.clone().slerp(quaternion(s.body.rotation()), alpha);
      const position = s.from.clone().lerp(vector(s.body.translation()), alpha).sub(s.offset.clone().applyQuaternion(rotation));
      s.bone.parent!.updateWorldMatrix(true, false);
      s.bone.position.copy(s.bone.parent!.worldToLocal(position));
      s.bone.quaternion.copy(s.bone.parent!.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(rotation));
      s.bone.updateWorldMatrix(false, false);
    }
    this.rig.root.updateWorldMatrix(true, true);
  }
  /** Frees the physics and gives up the rest transforms without restoring them: the body keeps its pose for whatever takes it over. */
  handOver(): RagdollRest { this.release(); const saved = this.saved; this.saved = []; return saved; }
  private release() { if (this.world) { this.world.free(); this.world = undefined; this.segments = []; this.headCollider = undefined; active.delete(this); } }
  dispose() {
    this.release();
    for (const s of this.saved) { s.bone.position.copy(s.position); s.bone.quaternion.copy(s.rotation); s.bone.scale.copy(s.scale); }
    this.saved = [];
  }
}
